import { useEffect } from 'react'
import ReactGA from 'react-ga4'
import { isGAInitialized } from './useGoogleAnalytics'

export function usePerformanceTracking(enabled: boolean = true) {
  useEffect(() => {
    if (!enabled || !isGAInitialized()) {
      console.log('⏭️ Performance Tracking: Skipped (not enabled or GA not initialized)')
      return
    }

    const observeNavigation = () => {
      const callback = (list: PerformanceObserverEntryList) => {
        list.getEntries().forEach((entry: any) => {
          if (entry.entryType === 'navigation') {
            const serverLatency = entry.responseStart - entry.requestStart
            const downloadTime = entry.responseEnd - entry.responseStart
            const totalLoadTime = entry.responseEnd - entry.requestStart
            const domContentLoaded = entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart
            const domInteractive = entry.domInteractive - entry.fetchStart

            ReactGA.event('performance_timing', {
              event_category: 'Performance',
              event_label: 'Server Latency',
              value: Math.round(serverLatency)
            })

            ReactGA.event('performance_timing', {
              event_category: 'Performance',
              event_label: 'Download Time',
              value: Math.round(downloadTime)
            })

            ReactGA.event('performance_timing', {
              event_category: 'Performance',
              event_label: 'Total Load Time',
              value: Math.round(totalLoadTime)
            })

            ReactGA.event('performance_timing', {
              event_category: 'Performance',
              event_label: 'DOM Content Loaded',
              value: Math.round(domContentLoaded)
            })

            ReactGA.event('performance_timing', {
              event_category: 'Performance',
              event_label: 'DOM Interactive',
              value: Math.round(domInteractive)
            })

            console.log('📊 Performance Metrics:', {
              'Server Latency': `${Math.round(serverLatency)}ms`,
              'Download Time': `${Math.round(downloadTime)}ms`,
              'Total Load Time': `${Math.round(totalLoadTime)}ms`,
              'DOM Content Loaded': `${Math.round(domContentLoaded)}ms`,
              'DOM Interactive': `${Math.round(domInteractive)}ms`
            })
          }
        })
      }

      try {
        const observer = new PerformanceObserver(callback)
        observer.observe({ entryTypes: ['navigation'] })
        return observer
      } catch (error) {
        console.warn('⚠️ PerformanceObserver not supported for navigation timing')
        return null
      }
    }

    const observePaint = () => {
      const callback = (list: PerformanceObserverEntryList) => {
        list.getEntries().forEach((entry) => {
          ReactGA.event('performance_timing', {
            event_category: 'Performance',
            event_label: entry.name,
            value: Math.round(entry.startTime)
          })

          console.log(`🎨 ${entry.name}: ${Math.round(entry.startTime)}ms`)
        })
      }

      try {
        const observer = new PerformanceObserver(callback)
        observer.observe({ entryTypes: ['paint'] })
        return observer
      } catch (error) {
        console.warn('⚠️ PerformanceObserver not supported for paint timing')
        return null
      }
    }

    const observeReactRender = () => {
      const callback = (list: PerformanceObserverEntryList) => {
        list.getEntries().forEach((entry) => {
          if (entry.name.includes('⚛') && entry.duration > 16) {
            ReactGA.event('performance_timing', {
              event_category: 'Performance',
              event_label: 'React Render',
              component: entry.name,
              value: Math.round(entry.duration)
            })

            console.log(`⚛️ React Render: ${entry.name} - ${Math.round(entry.duration)}ms`)
          }
        })
      }

      try {
        const observer = new PerformanceObserver(callback)
        observer.observe({ entryTypes: ['measure'] })
        return observer
      } catch (error) {
        console.warn('⚠️ PerformanceObserver not supported for measure timing')
        return null
      }
    }

    const navigationObserver = observeNavigation()
    const paintObserver = observePaint()
    const renderObserver = observeReactRender()

    return () => {
      navigationObserver?.disconnect()
      paintObserver?.disconnect()
      renderObserver?.disconnect()
    }
  }, [enabled])
}

export function trackCustomTiming(category: string, variable: string, value: number, label?: string) {
  if (!isGAInitialized()) {
    console.log('⚠️ Performance Tracking: GA not initialized, skipping custom timing')
    return
  }

  ReactGA.event('performance_timing', {
    event_category: category,
    event_label: variable,
    value: Math.round(value),
    ...(label && { label })
  })

  console.log(`📊 Custom Timing: ${category} - ${variable} = ${Math.round(value)}ms`)
}
